import { shallowEqual } from '@react-dnd/shallowequal';
import { useState } from 'react';
import { type DragLayerMonitor, useDragLayer } from 'react-dnd';

/**
 * Hook that minimizes severe performance issues with react-dnd-html5-backend.
 * The HTML5 backend re-renders while dragging way more than is necessary.
 * This hook wraps useDragLayer to only collect props when the props have changed.
 *
 * See these threads for a more in-depth explanation of the issue and solution:
 *   https://github.com/react-dnd/react-dnd/issues/2414#issuecomment-1025239409
 *   https://github.com/react-dnd/react-dnd/issues/592#issuecomment-825458440
 */
export const useEfficientDragLayer = <CollectedProps>(
  collect: (monitor: DragLayerMonitor) => CollectedProps,
): CollectedProps => {
  const collected = useDragLayer(collect);
  const [previousCollected, setPreviousCollected] =
    useState<CollectedProps>(collected);

  const [requestId, setRequestId] = useState<number>();
  if (requestId === undefined && !shallowEqual(collected, previousCollected)) {
    setPreviousCollected(collected);
    setRequestId(requestAnimationFrame(() => setRequestId(undefined)));
  }
  return previousCollected;
};
